/**
 * StandardizationTab — `Thông tin qui chuẩn` cho Production Report
 * (spec §5 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Áp skill `/table-style`: header gradient via `app-table-header-cell`,
 * cell ellipsis via `app-table-cell-text`, body striping via `app-table-wrap`.
 *
 * Group header (Before / Amount / After) dùng subtle bg để phân biệt với
 * column header chính (gradient).
 */
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import { STANDARDIZATION_ROWS, type StandardizationRow } from '../productionReportMock'
import { fmtMt, fmtPercentScaled, fmtPlain } from './formatters'
import {
  COLORS,
  sectionSubtitleSx,
  sectionTitleSx,
} from './styleTokens'

type StdGroup = 'before' | 'amount' | 'after'

interface StdColumn {
  key: keyof StandardizationRow
  label: string
  width: number
  group: StdGroup
  kind: 'text' | 'pct' | 'mt' | 'plain'
  sticky?: boolean
}

const STD_COLUMNS: StdColumn[] = [
  { key: 'batch', label: 'Batch', width: 140, group: 'before', kind: 'text', sticky: true },
  { key: 'stackId', label: 'Stack ID', width: 150, group: 'before', kind: 'text', sticky: true },
  { key: 'itemCode', label: 'Item code', width: 110, group: 'before', kind: 'text' },
  { key: 'receiveNw', label: 'Receive NW (Mt)', width: 140, group: 'before', kind: 'mt' },
  { key: 'deductPct', label: 'Deduct %', width: 100, group: 'amount', kind: 'pct' },
  { key: 'deductNw', label: 'Deduct NW (Mt)', width: 140, group: 'amount', kind: 'mt' },
  { key: 'itemAfter', label: 'Item sau qui chuẩn', width: 150, group: 'after', kind: 'text' },
  { key: 'stackIdAfter', label: 'Stack ID sau qui chuẩn', width: 170, group: 'after', kind: 'text' },
  { key: 'reason', label: 'Reason', width: 280, group: 'after', kind: 'text' },
  { key: 'stdBlacks', label: 'qui chuẩn Blacks', width: 130, group: 'after', kind: 'plain' },
  { key: 'stdBrokens', label: 'qui chuẩn Brokens', width: 140, group: 'after', kind: 'plain' },
  { key: 'stdFm', label: 'qui chuẩn FM', width: 120, group: 'after', kind: 'plain' },
  { key: 'blacksAfter', label: 'Blacks sau qui chuẩn', width: 160, group: 'after', kind: 'plain' },
  { key: 'brokensAfter', label: 'Brokens sau qui chuẩn', width: 170, group: 'after', kind: 'plain' },
  { key: 'fmAfter', label: 'FM sau qui chuẩn', width: 150, group: 'after', kind: 'plain' },
  { key: 'stdS18', label: 'qui chuẩn S18', width: 120, group: 'after', kind: 'plain' },
  { key: 's18After', label: 'S18 sau qui chuẩn', width: 150, group: 'after', kind: 'plain' },
  { key: 'stdS16', label: 'qui chuẩn S16', width: 120, group: 'after', kind: 'plain' },
  { key: 's16After', label: 'S16 sau qui chuẩn', width: 150, group: 'after', kind: 'plain' },
  { key: 'stdS13', label: 'qui chuẩn S13', width: 120, group: 'after', kind: 'plain' },
  { key: 's13After', label: 'S13 sau qui chuẩn', width: 150, group: 'after', kind: 'plain' },
]

const GROUP_LABEL: Record<StdGroup, string> = {
  before: 'Lot hàng trước qui chuẩn',
  amount: 'Khối lượng qui chuẩn',
  after: 'Sau qui chuẩn',
}

function fmtCell(v: unknown, kind: StdColumn['kind']): string {
  if (v == null || v === '') return ''
  switch (kind) {
    case 'pct': return fmtPercentScaled(Number(v), 1)
    case 'mt': return fmtMt(Number(v))
    case 'plain': return fmtPlain(Number(v), 2)
    default: return String(v)
  }
}

const groupHeaderCellSx: SxProps<Theme> = {
  fontSize: '0.65rem',
  fontWeight: 700,
  py: 0.4,
  px: 0.5,
  color: COLORS.textMuted,
  bgcolor: COLORS.bgSubtle,
  border: 'none',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  position: 'sticky',
  top: 0,
  zIndex: 4,
}

export default function StandardizationTab() {
  const stickyOffsets: Record<string, number> = {}
  let acc = 0
  for (const col of STD_COLUMNS) {
    if (col.sticky) {
      stickyOffsets[col.key] = acc
      acc += col.width
    }
  }

  const groupSpans: { group: StdGroup; span: number }[] = []
  for (const col of STD_COLUMNS) {
    const last = groupSpans[groupSpans.length - 1]
    if (last && last.group === col.group) last.span += 1
    else groupSpans.push({ group: col.group, span: 1 })
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={0.25}>
        <Typography sx={sectionTitleSx}>Thông tin qui chuẩn</Typography>
        <Typography sx={sectionSubtitleSx}>
          Lots phải qui chuẩn khi defect vượt tiêu chuẩn. Quality sau qui chuẩn = weighted average theo Receive NW + Deduct NW.
        </Typography>
      </Stack>

      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                {groupSpans.map((g, i) => (
                  <TableCell
                    key={`g-${i}`}
                    colSpan={g.span}
                    align="center"
                    sx={groupHeaderCellSx}
                  >
                    {GROUP_LABEL[g.group]}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {STD_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key as string}
                    align={col.kind === 'text' ? 'left' : 'right'}
                    className="app-table-header-cell"
                    style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                    sx={{
                      whiteSpace: 'nowrap',
                      top: 26,
                      ...(col.sticky && {
                        position: 'sticky',
                        left: stickyOffsets[col.key as string],
                        zIndex: 6,
                      }),
                    }}
                    title={col.label}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {STANDARDIZATION_ROWS.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={STD_COLUMNS.length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textSubtle }}>
                      No standardization rows for this PO.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                STANDARDIZATION_ROWS.map((row, idx) => (
                  <TableRow key={idx}>
                    {STD_COLUMNS.map((col) => {
                      const text = fmtCell(row[col.key], col.kind)
                      return (
                        <TableCell
                          key={col.key as string}
                          align={col.kind === 'text' ? 'left' : 'right'}
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            ...(col.sticky && {
                              position: 'sticky',
                              left: stickyOffsets[col.key as string],
                              zIndex: 2,
                              bgcolor: 'background.paper',
                              borderRight: `1px solid ${COLORS.border}`,
                            }),
                          }}
                          title={text}
                        >
                          <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }}>
                            {text}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}
